import React, { useRef, useState, useEffect } from "react";

import styles from "styles.module.css";
import {
	SalesNoteDetailGridItems,
	SalesNoteGridColumnItem,
} from "./utils/GridColumns";
import Filter from "./Filter";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";

import "./styles.css";
import { addMonths, addWeeks } from "date-fns";
import {
	deleteSalesNoteApi,
	getSalesNoteLinesApi,
	getSalesNotesApi,
	SalesNoteFilter,
} from "api/salesNote";
import GridCustomPage, { ColumnButtonProps } from "ui/grid/GridPage";
import { useTranslation } from "react-i18next";
import { ICON_NAMES } from "utils/constant";
import { useNavigate, useLocation } from "react-router";
import { PageTypePath } from "utils/pages";
import { GridCellProps } from "@progress/kendo-react-grid";
import CustomDetailedPage from "ui/grid/DetailPage";
import CustomButton, { TextButton } from "ui/Button";
import CustomPopup from "ui/modal/Popup";
import { Loading } from "ui/Loading";
import { usePageAccess } from "hooks/usePageAccess";
import { IconButtonActionType } from "interface";
import { SalesNote } from "interface/salesNote";
import SalesNotesMobileList from "./SalesNotesMobileList";
import Info from "ui/Info";

const pageSize = 60;

const SalesNotesPage = () => {
	const { t } = useTranslation();

	const gridRef = useRef<HTMLDivElement>(null);
	const checkItemsRef = useRef<HTMLDivElement>(null);
	const [checkedIds, setCheckIds] = useState<string[]>([]);
	const [showSelectedIds, setShowSelectedIds] = useState(false);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1600);
	const [mobileData, setMobileData] = useState<SalesNote[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);

	const mobileNextSkip = useRef(0);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const pendingDeleteNoteNumber = useRef<number | null>(null);

	const [filterOptions, setFilterOptions] = useState<SalesNoteFilter>({
		take: pageSize,
		skip: 0,
		fromDate: addWeeks(new Date(), -1),
		toDate: new Date(),
	});
	const navigate = useNavigate();

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const { pageAccess, loading } = usePageAccess();

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = (skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getSalesNotesApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as SalesNote[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	};

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);
		return () => window.removeEventListener("resize", updateMobileView);
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, filterOptions]);

	const openEditPage = (props: GridCellProps) => {
		const step = props.dataItem.currentStep;

		if (props.dataItem.noteStatusId === 4) {
			navigate(`/${PageTypePath.Signature}/${props.dataItem.noteNumber}`, {
				state: {
					quotaTypeCode: props.dataItem.quotaTypeCode,
					noteType: 0,
				},
			});
			return;
		}
		navigate(
			`/${PageTypePath.SalesNoteForm}/${props.dataItem.noteNumber}/${step}`
		);
	};

	const openEditByItem = (item: SalesNote) => {
		if (item.noteStatusId === 4) {
			navigate(`/${PageTypePath.Signature}/${item.noteNumber}`, {
				state: {
					quotaTypeCode: (item as any).quotaTypeCode,
					noteType: 0,
				},
			});
			return;
		}
		navigate(`/${PageTypePath.SalesNoteForm}/${item.noteNumber}/${(item as any).currentStep}`);
	};

	const openPdfByRow = (prop: GridCellProps) => {
		localStorage.setItem("noteNumber", prop.dataItem.noteNumber);
		window.open(`/${PageTypePath.Pdf}/${prop.dataItem.reportGuid}`, "_blank");
	};

	const deleteNote = (prop: GridCellProps) => {
		pendingDeleteNoteNumber.current = prop.dataItem.noteNumber;
		setShowDeleteConfirm(true);
	};

	const confirmDeleteNote = () => {
		if (pendingDeleteNoteNumber.current === null) return;
		deleteSalesNoteApi(pendingDeleteNoteNumber.current)
			.then(() => {
				setFilterOptions((prev) => ({ ...prev }));
			})
			.catch((error) => {
				console.error("Failed to delete note", error);
			})
			.finally(() => {
				pendingDeleteNoteNumber.current = null;
				setShowDeleteConfirm(false);
			});
	};

	const openPdfByItem = (item: SalesNote) => {
		localStorage.setItem("noteNumber", String(item.noteNumber));
		window.open(`/${PageTypePath.Pdf}/${(item as any).reportGuid}`, "_blank");
	};

	const itemAlreadyChecked = (id: string) => {
		return checkedIds.includes(id);
	};

	const toggleCheckedId = (id: string, status: number) => {
		if (!handleCanCheck(status)) {
			return;
		}
		setCheckIds((prev) => {
			if (prev.includes(id)) {
				return prev.filter((item) => item !== id);
			}
			return [...prev, id];
		});
	};

	const handleSelectedId = (id: string, noComma: boolean = true): string => {
		const firstPart = id.slice(0, 2);
		const secondPart = id.slice(2);

		const numberPart = Number(secondPart);
		return `${firstPart}-${numberPart}${noComma ? "" : ","}`;
	};

	const copyAllToClipboard = () => {
		const idsToCopy = checkedIds.map((id) => handleSelectedId(id)).join(", ");
		navigator.clipboard.writeText(idsToCopy).then(
			() => {
				console.log("Copied to clipboard successfully!");
			},
			(err) => {
				console.error("Could not copy text: ", err);
			}
		);
	};

	if (!pageAccess) {
		return <Loading show={true} />;
	}

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("SalesNotes")}</legend>
			</div>

			<Info
				title={t("Delete")}
				description={t("DeleteNoteConfirmation")}
				show={showDeleteConfirm}
				close={() => setShowDeleteConfirm(false)}
				actions={[
					{ text: t("Cancel"), btn: () => setShowDeleteConfirm(false) },
					{
						text: t("Yes"),
						btn: confirmDeleteNote,
						bgColor: "red",
						textColor: "white",
					},
				]}
			/>

			<div ref={checkItemsRef}>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
					handleShowSelectedIds={() => setShowSelectedIds(!showSelectedIds)}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{showSelectedIds && (
					<CustomPopup
						anchor={checkItemsRef}
						show={showSelectedIds}
						style={{ minWidth: "400px" }}
						onClose={() => setShowSelectedIds(!showSelectedIds)}
					>
						<div
							style={{
								padding: "10px",
								display: "flex",
								justifyContent: "space-between",
								height: "50px",
								fontSize: "16px",
								fontWeight: "bold",
								fontFamily: "Work Sans",
							}}
						>
							<p>Fangstsertifikatstring</p>
							<TextButton
								text={"X"}
								onClick={() => setShowSelectedIds(!showSelectedIds)}
								textStyle={{ paddingTop: "16px" }}
							/>
						</div>
						<div
							style={{
								padding: "10px",
								display: "flex",
								flexWrap: "wrap",
								maxWidth: "90%",
								gap: "10px",
							}}
						>
							{checkedIds.map((id) => {
								const isLast = checkedIds.indexOf(id) === checkedIds.length - 1;
								return <div key={id}>{handleSelectedId(id, isLast)}</div>;
							})}
						</div>
						<div
							style={{
								padding: "10px",
								display: "flex",
								justifyContent: "center",
							}}
						>
							<CustomButton
								onClick={copyAllToClipboard}
								label={"Kopier alle"}
							/>
						</div>
					</CustomPopup>
				)}
				{isMobileView ? (
					<SalesNotesMobileList
						items={mobileData}
						loading={mobileLoading}
						onEdit={(item) => openEditByItem(item)}
						onOpenPdf={(item) => openPdfByItem(item)}
						onToggleChecked={(item) =>
							toggleCheckedId(String(item.noteNumber), item.noteStatusId)
						}
						isChecked={(id) => itemAlreadyChecked(id)}
						canCheck={(status) => handleCanCheck(status)}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GridCustomPage
						dataKey="noteNumber"
						gridColumns={SalesNoteGridColumnItem()}
						detailPage={(prop) => (
							<CustomDetailedPage
								grids={SalesNoteDetailGridItems}
								dataApi={() =>
									getSalesNoteLinesApi({
										noteNumber: prop.dataItem.noteNumber,
										source: prop.dataItem.source,
									})
								}
							/>
						)}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						pageAccess={pageAccess}
						apiMethod={getSalesNotesApi}
						lastColWidth="220px"
						lastColBtns={[
							{
								onclick: openEditPage,
								iconName: ICON_NAMES.Edit,
								title: t("EditNote"),
								showConditionValue: 0,
								conditionField: "source",
								actionType: IconButtonActionType.Update,
							},
							{
								onclick: openEditPage,
								title: t("SignNote"),
								iconName: ICON_NAMES.Signature,
								showConditionValue: 4,
								conditionField: "noteStatusId",
								actionType: IconButtonActionType.Update,
							},
							{
								iconName: ICON_NAMES.Delete,
								title: t("Delete"),
								actionType: IconButtonActionType.Delete,
								handler: (prop) => {
									const statusId = prop.dataItem.noteStatusId;
									const visible = statusId === 1 || statusId === 2;
									return {
										onclick: deleteNote,
										alwaysShow: true,
										hidden: !visible,
									};
								},
							},
							{
								onclick: openPdfByRow,
								title: t("DownloadPdf"),
								iconName: ICON_NAMES.Download,
								alwaysShow: true,
							},
							{
								iconName: ICON_NAMES.CheckboxBlack,
								title: t("MarkForCC"),
								alwaysShow: true,

								handler: (prop) => {
									const colItem: ColumnButtonProps = {
										iconName: itemAlreadyChecked(prop.dataItem.noteNumber)
											? ICON_NAMES.Checkbox
											: ICON_NAMES.CheckboxBlack,
										style: {
											opacity: handleCanCheck(prop.dataItem.noteStatusId)
												? 1
												: 0.2,
										},
									};

									colItem.onclick = () => {
										if (!handleCanCheck(prop.dataItem.noteStatusId)) {
											return;
										}
										if (itemAlreadyChecked(prop.dataItem.noteNumber)) {
											const newCheckedIds = checkedIds.filter(
												(id) => id !== prop.dataItem.noteNumber
											);
											setCheckIds(newCheckedIds);
										} else {
											const newCheckedIds = [
												...checkedIds,
												prop.dataItem.noteNumber,
											];
											setCheckIds(newCheckedIds);
										}
									};
									return colItem;
								},
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default SalesNotesPage;

const handleCanCheck = (status: number) => {
	if (status === 1) return false;
	if (status === 2) return false;
	return true;
};
